import { CurrencyCode, MoneyAmountInPenceSigned } from "src/types/global";

/**
 * Formats an amount and currency.
 * Prefixes with "+" symbol if amount is positive.
 * Does not prefixes with "-" symbol if amount is negative.
 *
 * @param amount - An integer amount in pennies
 * @param currency - An ISO 4217 currency code
 * @param prefixPositive - If amount is positive, prefix with "+"
 * @param prefixNegative - If amount is negative, prefix with "-"
 */
export const formatMoneyAmountString = (
  amountInPenceSigned: MoneyAmountInPenceSigned,
  currency: CurrencyCode,
  prefixPositive = false,
  prefixNegative = false
): string => {
  const formattedAmount = new Intl.NumberFormat("en-UK", {
    style: "currency",
    // signDisplay: "never", // we can't use this property - see below
    currency: currency,
  }).format(amountInPenceSigned / 100);

  // COCO-13174
  // Using the Intl.NumberFormat constructor's `signDisplay` property crashes on Safari 14 on
  // macOS 10.14, so instead we use the default behaviour (sign display for negative numbers
  // only), and remove it manually
  //
  // Also remove the prefix if the value is a signed 0, so we don't get "-£0.00"
  // N.B. in JS, 0 === -0 === +0

  const isSignedZero =
    amountInPenceSigned === 0 &&
    (formattedAmount[0] === "-" || formattedAmount[0] === "+");
  const unsignedFormattedAmount =
    amountInPenceSigned < 0 || isSignedZero
      ? formattedAmount.substring(1)
      : formattedAmount;

  let prefix = "";
  if (amountInPenceSigned > 0) {
    prefix = prefixPositive ? "+" : prefix;
  } else if (amountInPenceSigned < 0) {
    prefix = prefixNegative ? "-" : prefix;
  }

  return `${prefix}${unsignedFormattedAmount}`;
};
